import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Building, 
  Users, 
  Award, 
  Clock, 
  Phone,
  Mail,
  MapPin,
  Home,
  Wrench,
  HardHat,
  CheckCircle,
  Star,
  Shield
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";

// Import images
import mississaugaConstruction from "@/assets/mississauga-construction.jpg";
import residentialConstruction from "@/assets/residential-construction.jpg";
import generalContractingProjects from "@/assets/general-contracting-projects.jpg";

const Mississauga = () => {
  const cityData = {
    name: "Mississauga",
    region: "Peel Region",
    population: "720K",
    slug: "mississauga-general-contracting",
    intro: "A major suburban center, Mississauga has diverse residential and commercial construction needs. Blue Knight Contracting delivers suburban development, industrial builds, and large-scale housing projects.",
    featured_services: [
      "Suburban Development",
      "Commercial Centers",
      "Industrial Construction",
      "Multi-Family Housing"
    ],
    nearby_areas: ["Toronto", "Brampton", "Oakville", "Milton"],
    cta: "Book your Mississauga contracting consultation today."
  };

  const faqData = [
    {
      question: "What types of suburban development do you handle in Mississauga?",
      answer: "We handle all types of suburban development in Mississauga including residential subdivisions, townhouse complexes, commercial plazas, and mixed-use developments throughout Peel Region."
    },
    {
      question: "Can you build commercial centers in Mississauga?",
      answer: "Yes, we specialize in commercial center construction including shopping plazas, office complexes, retail developments, and business parks in Mississauga and surrounding areas."
    },
    {
      question: "Do you handle industrial construction projects?",
      answer: "Absolutely. We provide comprehensive industrial construction services including warehouses, manufacturing facilities, distribution centers, and logistics hubs in Mississauga's industrial zones."
    },
    {
      question: "What areas around Mississauga do you serve?",
      answer: "We serve Mississauga and surrounding Peel Region including Toronto, Brampton, Oakville, Milton, and other communities in the western Greater Toronto Area."
    },
    {
      question: "Can you build multi-family housing in Mississauga?",
      answer: "Yes, we have extensive experience with multi-family housing construction including condominiums, apartment buildings, townhouse complexes, and affordable housing projects."
    },
    {
      question: "Do you work with Mississauga's planning and development requirements?",
      answer: "Yes, our team has deep knowledge of Mississauga's planning requirements, zoning bylaws, and development approval processes for efficient project delivery."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Blue Knight Contracting Mississauga",
    "description": "Professional general contracting and construction services in Mississauga and Peel Region. Specializing in suburban development, commercial centers, and multi-family housing.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Mississauga",
      "addressRegion": "Ontario",
      "addressCountry": "Canada"
    },
    "telephone": "+1-647-760-8854",
    "url": "https://blueknightcontracting.ca/mississauga",
    "areaServed": {
      "@type": "City",
      "name": "Mississauga"
    },
    "serviceType": [
      "General Contracting Mississauga",
      "Suburban Development",
      "Commercial Centers Mississauga",
      "Industrial Construction",
      "Multi-Family Housing"
    ]
  };

  const constructionServices = [
    {
      title: "Suburban Development",
      description: "Comprehensive suburban development projects including residential communities and infrastructure.",
      icon: Home,
      features: [
        "Residential subdivisions",
        "Townhouse developments",
        "Community infrastructure",
        "Utility installations",
        "Landscaping & amenities",
        "Sustainable development"
      ]
    },
    {
      title: "Commercial Centers",
      description: "Modern commercial developments including shopping centers and business complexes.",
      icon: Building,
      features: [
        "Shopping plazas",
        "Office complexes",
        "Business parks",
        "Retail developments",
        "Mixed-use projects",
        "Parking facilities"
      ]
    },
    {
      title: "Industrial Construction",
      description: "Large-scale industrial facilities for manufacturing and distribution operations.",
      icon: Wrench,
      features: [
        "Manufacturing facilities",
        "Warehouse construction",
        "Distribution centers",
        "Logistics hubs",
        "Industrial parks",
        "Processing facilities"
      ]
    },
    {
      title: "Multi-Family Housing",
      description: "Residential housing projects from condominiums to affordable housing developments.",
      icon: HardHat,
      features: [
        "Condominium buildings",
        "Apartment complexes",
        "Affordable housing",
        "Senior housing",
        "Student residences",
        "Social housing"
      ]
    }
  ];

  return (
    <>
      <SchemaMarkup type="LocalBusiness" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Mississauga General Contracting
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  General Contracting & Construction in Mississauga
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  {cityData.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get Mississauga Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Call: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Population: {cityData.population}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <span>{cityData.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Licensed & Insured</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={mississaugaConstruction} 
                  alt="Mississauga City Centre skyline with new developments" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Mississauga Construction Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Professional Construction Services in Mississauga
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From suburban development to industrial construction, we deliver comprehensive building solutions across Peel Region.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {constructionServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Services List */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Specialties</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Featured Mississauga Construction Services
              </h2>
              <p className="text-xl text-muted-foreground">
                Specialized construction services for Canada's sixth-largest city and major suburban center.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cityData.featured_services.map((service, index) => (
                <Card key={index} className="group hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                      <CheckCircle className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{service}</h3>
                    <p className="text-muted-foreground text-sm">
                      Professional {service.toLowerCase()} services in Mississauga and Peel Region.
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby Service Areas */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Peel Region Service Areas</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Serving Mississauga and Surrounding Areas
              </h2>
              <p className="text-xl text-muted-foreground">
                Professional construction services throughout Peel Region and the western GTA.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <Card className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-primary/5">
                  <div className="flex items-center justify-center gap-2">
                    <Star className="h-4 w-4 text-primary" />
                    <span className="font-semibold">Mississauga</span>
                  </div>
                </Card>
                {cityData.nearby_areas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Mississauga Construction FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about construction services in Mississauga and Peel Region.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Start Your Mississauga Construction Project?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                {cityData.cta} Get professional construction services from Mississauga's trusted general contractors.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">Mississauga & Peel Region</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get Mississauga Construction Quote</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our Mississauga Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default Mississauga;